import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Trans } from 'react-i18next';
import path from 'path';

import { analytics } from 'core/analytics';
import { Link } from 'core/routing';

import { useClientStore, useRouterStore } from 'apps/hooks';
import { useSharedFileManager } from 'apps/share/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';

import Logo from 'components/Logo';
import Icon from 'components/Icon';
import Select from 'components/Select';
import Tooltip from 'components/Tooltip';
import { Dropdown } from 'libs/rendering/gltf/components/headerDropdown';
// import Collaborators from 'libs/rendering/components/header/collaborators';
import { PortalLink } from 'apps/client/components/PortalLink';

import { Accordion, AccordionItem, AccordionPanel } from '@chakra-ui/react';
import styled from 'styled-components';

import {
  HeaderStyled,
  BurgerWrapper,
  MainRowWrapper,
  Crumbs,
  CrumbDivider,
  NavStyled,
  ButtonStyled,
  MakeButton,
  CloseButton,
  SignInUpButton,
} from './header.styled';

const getCloseRoute = routerStore => {
  const params = routerStore.params.toJSON();

  if (!((params.profile || params.initiative) && params.basepath)) {
    return { name: 'home', params: {} };
  }

  const basepathSegs = [];
  if (params.basepath) {
    params.basepath.split('/');
  }
  const spaceType = params.profile ? 'profile' : 'initiative';
  const closeRouteName =
    basepathSegs.length > 1
      ? `${spaceType}.project.files.directory`
      : `${spaceType}.project.files`;
  const closeParams =
    basepathSegs.length > 1
      ? {
          [spaceType]: params.profile || params.initiative,
          slug: params.slug,
          basepath: params.basepath
            .split(/\/(?=[^\/]+$)/)
            .slice(0, -1)
            .join('/'),
        }
      : {
          [spaceType]: params.profile || params.initiative,
          slug: params.slug,
        };

  return { name: closeRouteName, params: closeParams };
};

const Breadcrumbs = observer(() => {
  const routerStore = useRouterStore();
  const clientStore = useClientStore();

  const params = routerStore.params.toJSON();
  const { route } = routerStore;

  if (route.name === 'workspace.share-file' || route.name.endsWith('rfq')) {
    const { signedInProfile } = clientStore;

    if (!signedInProfile) return null;
    return (
      <Crumbs>
        <Link {...signedInProfile.route}>{signedInProfile.username}</Link> /{' '}
        <Trans>
          {route.name === 'workspace.share-file' && (
            <>
              <Link name='home'>shared</Link> / new
            </>
          )}
          {route.name.endsWith('rfq') && (
            <>
              <PortalLink
                name='workspace.rfqs'
                params={{ spacePath: clientStore.activeSpace?.path }}
              >
                RFQs
              </PortalLink>
              <span>{` / ${route.params.rfqId}`}</span>
            </>
          )}
          {route.name.endsWith('orders') && (
            <>
              <PortalLink
                name='workspace.orders'
                params={{ spacePath: clientStore.activeSpace?.path }}
              >
                Orders
              </PortalLink>
              <span>{` / ${route.params.rfqId}`}</span>
            </>
          )}
        </Trans>
      </Crumbs>
    );
  }

  if (route.name.endsWith('shared.file')) {
    const sharedFileManager = useSharedFileManager();
    const filename = sharedFileManager?.sharedFile?.name || '...';

    const parentSpace = sharedFileManager?.sharedFile?.inSpace?.parent;
    if (!parentSpace) return null;
    return (
      <Crumbs>
        <Link {...parentSpace.route}>{parentSpace.path}</Link> /{' '}
        <Trans>
          <Link name='home'>shared</Link> / {filename}
        </Trans>
      </Crumbs>
    );
  }

  if (!params.basepath) return null;

  const spaceType = route.name.split('.')[0];
  const splitPath = params.basepath.split('/');
  const pathElements =
    splitPath.length > 2
      ? ['...', splitPath[splitPath.length - 2]]
      : splitPath.slice(0, -1);

  const spaceRoute = {
    name: spaceType,
    params: { [spaceType]: params[spaceType] },
  };
  const projectRoute = {
    name: `${spaceRoute.name}.project`,
    params: { ...spaceRoute.params, slug: params.slug },
  };
  const filesRoute = {
    name: `${projectRoute.name}.files`,
    params: projectRoute.params,
  };
  const directoryPathOutput = path.dirname(params.basepath);
  const directoryPath = directoryPathOutput === '.' ? '' : directoryPathOutput;
  const folderRoute = {
    name: `${filesRoute.name}.directory`,
    params: { ...projectRoute.params, basepath: directoryPath },
  };
  const filename = path.basename(params.basepath);

  return (
    <Crumbs>
      <li>
        <Link {...spaceRoute}>{params[spaceType]}</Link>
        <CrumbDivider>/</CrumbDivider>
        <Link {...projectRoute}>{params.slug}</Link>
        <CrumbDivider>/</CrumbDivider>
        <Link {...filesRoute}>files</Link>
        <CrumbDivider>/</CrumbDivider>
        {pathElements.map(pathPart => (
          <>
            <Link {...folderRoute}>{pathPart}</Link>
            <CrumbDivider>/</CrumbDivider>
          </>
        ))}
        <strong>{filename}</strong>
      </li>
    </Crumbs>
  );
});

const Header = observer(() => {
  const clientStore = useClientStore();
  const routerStore = useRouterStore();
  const sharedFileManager = useSharedFileManager();
  const viewerControl = useViewerControl();

  const [showBurger, setShowBurger] = useState(false);

  // - this header is only for the old header, if the user is signed in
  // we are using the wf2.0 header, so we return null here
  if (clientStore.signedInProfile) return null;

  // const usersActivity = contextManager?.usersActivity;
  const revisionsConnection = sharedFileManager?.revisionsConn;
  const { route } = routerStore;
  const { params } = route;

  // construct params for current folder
  // navigate to rfq list if at rfq page.
  const closeRoute = route.name.includes('rfq')
    ? {
        name: 'workspace.rfqs',
        params: { spacePath: clientStore.activeSpace?.path },
      }
    : route.name.includes('order')
    ? {
        name: 'workspace.orders',
        params: { spacePath: clientStore.activeSpace?.path },
      }
    : getCloseRoute(routerStore);

  // Navigate to previous page, instead of current folder if the user came
  // via a Preview, so we can take him back to the right place.
  const cameViaPreview = params.viaPreview === 'true';

  // FIXME - connect to space selector once that's available
  // const sharedFileSpace = clientStore.signedInProfile?.inSpace;

  const makeButtonRoutes = [
    'profile.shared.file',
    'profile.project.file',
    'initiative.project.file',
  ];

  // Note: being purposefully verbose here for readibility. Ternery operator was
  // too complex.
  let showMakeButton = false;
  if (
    viewerControl.isSinglePartFile &&
    makeButtonRoutes.includes(routerStore.route?.name)
  ) {
    if (!routerStore.route.params.panel === 'rfq') {
      showMakeButton = true;
    }
  }

  return (
    <HeaderStyled>
      {routerStore.route?.name?.includes('shared') ? (
        <Select
          type='burger'
          main={
            <BurgerWrapper
              onMouseEnter={() => setShowBurger(true)}
              onMouseLeave={() => setShowBurger(false)}
            >
              {showBurger ? (
                <Icon name='menu' colorPath='wfWhite' size='medium' />
              ) : (
                <Logo size='22px' negativeVersion />
              )}
            </BurgerWrapper>
          }
          options={[
            {
              icon: 'dashboard',
              label: <Trans>Dashboard</Trans>,
              route: 'home',
            },
            {
              icon: 'share',
              label: <Trans>Share new file</Trans>,
              route: 'workspace.share-file',
            },
          ]}
        />
      ) : (
        <BurgerWrapper>
          <Link name='home'>
            <Logo size='22px' negativeVersion />
          </Link>
        </BurgerWrapper>
      )}
      <MainRowWrapper>
        <Breadcrumbs />
        {revisionsConnection?.totalCount > 1 && (
          <Dropdown
            revisionsConnection={revisionsConnection}
            selectedRevision={sharedFileManager.sharedFile?.revision}
          />
        )}
        {sharedFileManager?.sharedFile?.canUpdate && (
          <ButtonStyled
            plain
            borderRight
            text={<Trans>Upload New Version</Trans>}
            ariaLabel='Upload New Version'
            size='xsmall'
            onClick={sharedFileManager.OpenRevisionModal}
            hideOnMobile
          />
        )}
      </MainRowWrapper>
      <NavStyled>
        <Tooltip id='header-nav' delayShow={500} place='bottom' />
        {/* {usersActivity && makeButtonRoutes.includes(route.name) && (
          <Collaborators
            usersActivity={usersActivity}
            sharedFileManager={sharedFileManager}
          />
        )} */}

        {!clientStore.signedInProfile && (
          <Link name='home.signIn' params={{ next: clientStore.next() }}>
            <SignInUpButton
              ariaLabel='Sign in / Sign up'
              size='xsmall'
              appearance='plain'
              text={<Trans>Sign in / Sign up</Trans>}
            />
          </Link>
        )}

        {showMakeButton && viewerControl.assemblyReady && (
          <Link
            name={routerStore.route.name}
            params={{ ...routerStore.route.params, panel: 'rfq' }}
            onClick={() => {
              analytics.sendEvent(
                'make-part-click-cad-rooms',
                {},
                { ga: true },
              );
            }}
          >
            <MakeButton
              icon='cube'
              iconPosition='right'
              text={<Trans>Make</Trans>}
            />
          </Link>
        )}

        {cameViaPreview ? (
          <CloseButton
            icon='close'
            ariaLabel='Close'
            size='xsmall'
            appearance='plain'
            removeBorder={showMakeButton}
            onClick={() => window.history.go(-1)}
          />
        ) : (
          <PortalLink
            {...closeRoute}
            onClick={() => {
              analytics.sendEvent('viewer-close');
            }}
          >
            <CloseButton
              icon='close'
              ariaLabel='Close'
              size='xsmall'
              appearance='plain'
              removeBorder={showMakeButton}
            />
          </PortalLink>
        )}
      </NavStyled>
    </HeaderStyled>
  );
});

export const StyledAccordion = styled(Accordion)`
  color: white;
  &:focus {
    outline: 0 !important;
  }
`;
export const StyledAccordionItem = styled(AccordionItem)`
  border-top: black;
`;
export const StyledAccordionPanel = styled(AccordionPanel)`
  display: flex;
  flex-direction: column;
  padding: 0 !important;
`;

export const DownloadTextMain = styled.p`
  font-weight: 300;
`;
export const DownloadTextSecondary = styled.p`
  font-weight: 300;
  opacity: 0.5;
`;

export default Header;
